import { useState } from "react";

/* ── Demo registry data ── */
interface RegistryEntry {
  id: string;
  variable_name: string;
  source_type: "expert" | "data" | "synthetic";
  resolution: "data_match" | "proxy" | "correlation" | "synthetic";
  expert_name: string;
  source_quote: string;
  mechanism: string;
  certainty: number;
  prior_mean: number;
  prior_std: number;
  posterior_mean: number | null;
  posterior_std: number | null;
  status: "candidate" | "validated" | "deployed" | "rejected";
  created_at: string;
}

const DEMO_REGISTRY: RegistryEntry[] = [
  {
    id: "var_001",
    variable_name: "Parental_Involvement_Score",
    source_type: "expert",
    resolution: "synthetic",
    expert_name: "Sarah Hartwell",
    source_quote: "In my 20 years of family law, parental involvement is the single strongest predictor...",
    mechanism: "Active parenting correlates with favorable custody outcomes through consistent visitation and engagement.",
    certainty: 0.82,
    prior_mean: 0.40,
    prior_std: 0.12,
    posterior_mean: 0.31,
    posterior_std: 0.06,
    status: "deployed",
    created_at: "2026-03-15",
  },
  {
    id: "var_002",
    variable_name: "Domestic_Violence_Risk",
    source_type: "expert",
    resolution: "correlation",
    expert_name: "Dr. Diana Washington",
    source_quote: "Any documented DV history shifts the calculus entirely — it's nearly deterministic below a threshold.",
    mechanism: "DV history triggers mandatory safety evaluations and supervised visitation requirements.",
    certainty: 0.91,
    prior_mean: 0.65,
    prior_std: 0.08,
    posterior_mean: 0.58,
    posterior_std: 0.04,
    status: "validated",
    created_at: "2026-03-15",
  },
  {
    id: "var_003",
    variable_name: "Child_Attachment_Quality",
    source_type: "expert",
    resolution: "synthetic",
    expert_name: "Dr. Michael Kwan",
    source_quote: "Attachment security measured via the Strange Situation protocol predicts long-term adjustment.",
    mechanism: "Secure attachment to primary caregiver mediates child wellbeing through emotional regulation capacity.",
    certainty: 0.74,
    prior_mean: 0.55,
    prior_std: 0.15,
    posterior_mean: null,
    posterior_std: null,
    status: "candidate",
    created_at: "2026-03-16",
  },
  {
    id: "var_004",
    variable_name: "Income_Disparity_Index",
    source_type: "data",
    resolution: "data_match",
    expert_name: "James Thornton",
    source_quote: "When income disparity exceeds 3:1, the lower-earning spouse faces systematic disadvantage in litigation.",
    mechanism: "Financial asymmetry affects legal representation quality and negotiation power.",
    certainty: 0.68,
    prior_mean: 0.35,
    prior_std: 0.18,
    posterior_mean: 0.29,
    posterior_std: 0.07,
    status: "validated",
    created_at: "2026-03-14",
  },
  {
    id: "var_005",
    variable_name: "Geographic_Stability",
    source_type: "expert",
    resolution: "proxy",
    expert_name: "Hon. Rebecca Paulson",
    source_quote: "Judges weight stability heavily — a parent who's moved 3 times in 2 years raises red flags.",
    mechanism: "Residential stability signals environment consistency for the child's schooling and social development.",
    certainty: 0.60,
    prior_mean: 0.30,
    prior_std: 0.20,
    posterior_mean: null,
    posterior_std: null,
    status: "candidate",
    created_at: "2026-03-17",
  },
  {
    id: "var_006",
    variable_name: "Substance_Abuse_History",
    source_type: "expert",
    resolution: "synthetic",
    expert_name: "Dr. Adriana Morales",
    source_quote: "Active substance abuse is disqualifying, but recovery history of 2+ years is viewed favorably.",
    mechanism: "Substance abuse directly impairs parenting capacity and child safety.",
    certainty: 0.88,
    prior_mean: 0.60,
    prior_std: 0.10,
    posterior_mean: 0.52,
    posterior_std: 0.05,
    status: "deployed",
    created_at: "2026-03-13",
  },
];

/* ── Status config ── */
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  candidate:  { label: "Candidate",  color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  validated:  { label: "Validated",  color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
  deployed:   { label: "Deployed",   color: "#10b981", bg: "rgba(16,185,129,0.1)" },
  rejected:   { label: "Rejected",   color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
};

const RESOLUTION_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  data_match:  { label: "Data Match",  color: "#2563eb", icon: "DB" },
  proxy:       { label: "Proxy",       color: "#7c3aed", icon: "PX" },
  correlation: { label: "Correlation", color: "#0891b2", icon: "CR" },
  synthetic:   { label: "Synthetic",   color: "#059669", icon: "SN" },
};

/* ── Component ── */
export default function VariableRegistry() {
  const [entries] = useState<RegistryEntry[]>(DEMO_REGISTRY);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const filtered = filterStatus ? entries.filter(e => e.status === filterStatus) : entries;

  const statusCounts = {
    candidate: entries.filter(e => e.status === "candidate").length,
    validated: entries.filter(e => e.status === "validated").length,
    deployed: entries.filter(e => e.status === "deployed").length,
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{
        padding: "20px 32px 14px",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        flexShrink: 0,
      }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          Variable Registry
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(0,0,0,0.06)" }}>
            {entries.length} variables
          </span>
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Accumulated expert knowledge — all extracted variables across interviews
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ padding: "14px 32px", display: "flex", gap: 10, flexShrink: 0 }}>
        {(["candidate", "validated", "deployed"] as const).map(status => {
          const cfg = STATUS_CONFIG[status];
          const count = statusCounts[status];
          const isActive = filterStatus === status;
          return (
            <div
              key={status}
              onClick={() => setFilterStatus(isActive ? null : status)}
              style={{
                flex: 1, padding: "12px 16px", borderRadius: 10, cursor: "pointer",
                border: `1.5px solid ${isActive ? cfg.color : "rgba(0,0,0,0.08)"}`,
                background: isActive ? cfg.bg : "#fff",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: cfg.color }}>
                {count}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: cfg.color, marginTop: 2 }}>
                {cfg.label}
              </div>
            </div>
          );
        })}
        <div style={{
          flex: 1, padding: "12px 16px", borderRadius: 10,
          border: "1.5px solid rgba(0,0,0,0.08)", background: "#fff",
        }}>
          <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace" }}>
            {entries.filter(e => e.posterior_mean != null).length}
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.4)", marginTop: 2 }}>
            With Posterior
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: "auto", padding: "0 32px 40px" }}>
        {/* Table header */}
        <div style={{
          display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 80px",
          padding: "8px 16px", marginBottom: 4,
          fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
          color: "rgba(0,0,0,0.35)",
        }}>
          <span>Variable</span>
          <span>Expert</span>
          <span>Resolution</span>
          <span>Prior</span>
          <span>Posterior</span>
          <span>Certainty</span>
          <span>Status</span>
        </div>

        {/* Rows */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {filtered.map(entry => {
            const isExpanded = expandedId === entry.id;
            const statusCfg = STATUS_CONFIG[entry.status];
            const resCfg = RESOLUTION_CONFIG[entry.resolution];

            return (
              <div key={entry.id} style={{
                border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10,
                overflow: "hidden", background: "#fff",
                transition: "all 0.15s ease",
                boxShadow: isExpanded ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
              }}>
                {/* Row */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                  style={{
                    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 80px",
                    padding: "12px 16px", cursor: "pointer",
                    alignItems: "center", fontSize: 12,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{entry.variable_name}</div>
                    <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)", marginTop: 1 }}>{entry.created_at}</div>
                  </div>
                  <span style={{ fontSize: 11, color: "rgba(0,0,0,0.6)" }}>{entry.expert_name.split(" ")[1]}</span>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                    background: resCfg.color + "14", color: resCfg.color,
                    width: "fit-content",
                  }}>
                    {resCfg.icon} {resCfg.label}
                  </span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                    N({entry.prior_mean.toFixed(2)}, {entry.prior_std.toFixed(2)})
                  </span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: entry.posterior_mean != null ? "#000" : "rgba(0,0,0,0.2)" }}>
                    {entry.posterior_mean != null
                      ? `N(${entry.posterior_mean.toFixed(2)}, ${entry.posterior_std!.toFixed(2)})`
                      : "—"
                    }
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ flex: 1, height: 4, background: "rgba(0,0,0,0.06)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${entry.certainty * 100}%`, background: "#000", borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, minWidth: 28 }}>
                      {(entry.certainty * 100).toFixed(0)}%
                    </span>
                  </div>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                    background: statusCfg.bg, color: statusCfg.color,
                    textAlign: "center",
                  }}>
                    {statusCfg.label}
                  </span>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ padding: "0 16px 16px", borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                      {/* Expert quote */}
                      <div style={{
                        padding: "12px 14px", borderRadius: 8,
                        borderLeft: "3px solid rgba(0,0,0,0.1)",
                        background: "rgba(0,0,0,0.02)",
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.35)", marginBottom: 6 }}>
                          Expert Quote — {entry.expert_name}
                        </div>
                        <div style={{ fontSize: 11.5, lineHeight: 1.55, fontStyle: "italic", color: "rgba(0,0,0,0.65)" }}>
                          "{entry.source_quote}"
                        </div>
                      </div>

                      {/* Mechanism */}
                      <div style={{
                        padding: "12px 14px", borderRadius: 8,
                        borderLeft: "3px solid rgba(99,102,241,0.3)",
                        background: "rgba(99,102,241,0.03)",
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(99,102,241,0.6)", marginBottom: 6 }}>
                          Mechanism
                        </div>
                        <div style={{ fontSize: 11.5, lineHeight: 1.55, color: "rgba(0,0,0,0.65)" }}>
                          {entry.mechanism}
                        </div>
                      </div>
                    </div>

                    {/* Prior → Posterior shift */}
                    {entry.posterior_mean != null && (
                      <div style={{
                        marginTop: 12, padding: "12px 16px", borderRadius: 8,
                        background: "rgba(16,185,129,0.04)", border: "1px solid rgba(16,185,129,0.1)",
                        display: "flex", alignItems: "center", gap: 20,
                      }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: "rgba(99,102,241,0.7)", fontWeight: 700, textTransform: "uppercase" }}>Prior</div>
                          <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {entry.prior_mean.toFixed(3)}
                          </div>
                          <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)" }}>σ={entry.prior_std.toFixed(3)}</div>
                        </div>
                        <svg width="24" height="12" viewBox="0 0 24 12">
                          <line x1="0" y1="6" x2="18" y2="6" stroke="#10b981" strokeWidth="2" />
                          <polygon points="18,2 24,6 18,10" fill="#10b981" />
                        </svg>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: "#10b981", fontWeight: 700, textTransform: "uppercase" }}>Posterior</div>
                          <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {entry.posterior_mean.toFixed(3)}
                          </div>
                          <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)" }}>σ={entry.posterior_std!.toFixed(3)}</div>
                        </div>
                        <div style={{ marginLeft: "auto", fontSize: 11, color: "rgba(0,0,0,0.5)", lineHeight: 1.5 }}>
                          Shrinkage: <strong>{Math.round(Math.abs(entry.prior_mean - entry.posterior_mean) / Math.abs(entry.prior_mean) * 100)}%</strong> toward data
                          <br />
                          Precision gain: <strong>{(entry.prior_std / entry.posterior_std!).toFixed(1)}×</strong> tighter
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
